// « Relancer » une tâche du Job Center = créer une NOUVELLE tâche de même type,
// même libellé et même payload par le chemin normal (enqueue-job). Elle est
// donc autorisée par can_enqueue_job avec le JWT de l'utilisateur, lancée par
// le webhook INSERT de jobs (job-trigger) et dédupliquée comme toute autre.
//
// L'ancienne fonction retry-job remettait la ligne en « pending » avec le
// client service : elle contournait can_enqueue_job, effaçait le résultat, et
// un UPDATE ne déclenche pas le webhook AFTER INSERT (la tâche attendait le
// watchdog). Elle répondait de toute façon toujours 401.
import { JobKinds, type JobKind } from "../../supabase/functions/_shared/jobKinds";
import type { EnqueueJobPayload } from "../../services/jobService";

/**
 * Types qu'un utilisateur peut lancer lui-même : miroir EXACT des branches
 * WHEN de public.can_enqueue_job (migration
 * 20261012120000_job_enqueue_authorization.sql). Tout le reste y tombe dans
 * ELSE false : chunks et merges (créés par les workers), tirage au sort
 * (enqueue_calendar_draw_pdf porte les verrous d'état), FAF auto-gérés
 * (faf.extract_ai / faf.sync_match). Les proposer donnerait un refus garanti.
 *
 * Le serveur reste juge : un type de cette liste peut être refusé pour ce
 * payload (rôle retiré, désignation supprimée…) — refus montré en toast.
 */
export const RETRYABLE_JOB_TYPES: ReadonlySet<string> = new Set<JobKind>([
  JobKinds.MissionOrdersSinglePdf,
  JobKinds.MissionOrdersSingleEmail,
  JobKinds.MissionOrdersBulkPdf,
  JobKinds.MatchSheetsBulkEmail,
  JobKinds.MessagingBulkEmail,
  JobKinds.DisciplinaryPvPdf,
  JobKinds.SanctionsPdf,
  JobKinds.StatementsSinglePdf,
  JobKinds.StatementsBulkPdf,
  JobKinds.CategoryRecapPdf,
  JobKinds.CalendarPdf,
  JobKinds.CalendarOptimize,
  JobKinds.FafAuditFixGoals,
  JobKinds.AccountAuditCreate,
  JobKinds.DistanceRefresh,
]);

export const RETRYABLE_JOB_STATUSES: ReadonlySet<string> = new Set(["failed", "cancelled"]);

/**
 * Envois d'e-mails : progress compte les messages déjà partis. Relancer une
 * tâche interrompue en cours d'envoi renverrait aussi ceux-là ; on ne la
 * propose donc que si rien n'est parti.
 */
export const EMAIL_JOB_TYPES: ReadonlySet<string> = new Set<JobKind>([
  JobKinds.MissionOrdersSingleEmail,
  JobKinds.MatchSheetsBulkEmail,
  JobKinds.MessagingBulkEmail,
]);

const hasSentEmails = (type: string, progress: number | null | undefined): boolean =>
  EMAIL_JOB_TYPES.has(type) && typeof progress === "number" && progress > 0;

export const isRetryableJobType = (type: string | null | undefined): boolean =>
  typeof type === "string" && RETRYABLE_JOB_TYPES.has(type);

/** Ligne optimiste créée côté client avant la réponse d'enqueue-job. */
export const isPlaceholderJobId = (id: string): boolean => id.startsWith("tmp_");

/**
 * Faut-il afficher « Relancer » pour cette ligne du Job Center ? Tâche de
 * l'utilisateur, terminée en échec ou annulée, d'un type qu'il peut lancer.
 */
export function canOfferJobRetry(
  job: { id: string; type: string; status: string; userId?: string | null; completed?: number | null },
  userId: string | null | undefined,
): boolean {
  if (!userId || !job || isPlaceholderJobId(job.id)) return false;
  if (job.userId !== userId) return false;
  if (hasSentEmails(job.type, job.completed)) return false;
  return RETRYABLE_JOB_STATUSES.has(job.status) && isRetryableJobType(job.type);
}

/** Colonnes lues sur la ligne d'origine avant de la relancer. */
export const RETRY_SOURCE_COLUMNS =
  "id, user_id, type, label, payload, total, progress, dedupe_key, status, is_chunk_job, parent_chunk_id";

export interface RetrySourceRow {
  id: string;
  user_id: string | null;
  type: string;
  label: string | null;
  payload: unknown;
  total: number | null;
  progress?: number | null;
  dedupe_key: string | null;
  status: string;
  is_chunk_job: boolean | null;
  parent_chunk_id: string | null;
}

export type JobRetryErrorCode =
  | "not_found"
  | "not_owner"
  | "not_retryable_status"
  | "not_retryable_type"
  | "emails_already_sent";

export class JobRetryError extends Error {
  readonly code: JobRetryErrorCode;
  constructor(code: JobRetryErrorCode, message?: string) {
    super(message ?? `Job retry refused: ${code}`);
    this.name = "JobRetryError";
    this.code = code;
  }
}

/**
 * Clés de payload qui décrivent UNE exécution et ne doivent pas être rejouées.
 * Aucun type relançable n'en porte aujourd'hui (vérifié sur les payloads en
 * production et les workers : aucun ne réécrit jobs.payload) ; les chunks, qui
 * portent parentJobId / chunkIndex / staggerDelay, ne sont pas relançables.
 * Gardé en défense si un worker se mettait à annoter le payload.
 */
const RUN_SCOPED_PAYLOAD_KEYS = ["parentJobId", "chunkIndex", "totalChunks", "chunkStoragePaths", "staggerDelay"];

const stripRunScopedKeys = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return {};
  const copy: Record<string, unknown> = { ...(payload as Record<string, unknown>) };
  for (const k of RUN_SCOPED_PAYLOAD_KEYS) delete copy[k];
  return copy;
};

/**
 * Requête d'enqueue équivalente à la tâche d'origine. Lève JobRetryError si
 * la ligne ne peut pas être relancée par cet utilisateur.
 *
 * dedupe_key est repris tel quel : le seul type qui en pose
 * (calendar.optimize) le dérive du groupe et de la saison, pas de l'heure ;
 * enqueue-job ne compare qu'aux tâches pending/processing, donc l'ancienne
 * ligne en échec ne bloque pas, et une tâche identique déjà en cours est
 * réutilisée au lieu d'en lancer une seconde.
 */
export function buildRetryEnqueueRequest(
  row: RetrySourceRow | null | undefined,
  userId: string,
): EnqueueJobPayload {
  if (!row) throw new JobRetryError("not_found");
  if (!userId || row.user_id !== userId) throw new JobRetryError("not_owner");
  if (!RETRYABLE_JOB_STATUSES.has(row.status)) throw new JobRetryError("not_retryable_status");
  if (row.is_chunk_job || row.parent_chunk_id || !isRetryableJobType(row.type)) {
    throw new JobRetryError("not_retryable_type");
  }
  if (hasSentEmails(row.type, row.progress)) throw new JobRetryError("emails_already_sent");
  const request: EnqueueJobPayload = {
    type: row.type as JobKind,
    label: row.label || row.type,
    payload: stripRunScopedKeys(row.payload) as EnqueueJobPayload["payload"],
  };
  if (typeof row.total === "number" && row.total > 0) request.total = row.total;
  if (row.dedupe_key) request.dedupe_key = row.dedupe_key;
  return request;
}
